import { useState, useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { projectsApi, EditableToken } from "../api";
import {
  Paintbrush,
  Type,
  Maximize2,
  Square,
  Sun,
  Ruler,
  Save,
  RotateCcw,
  Download,
  ChevronDown,
  ChevronRight,
  Loader2,
  Check,
  AlertTriangle,
  Palette,
  Zap,
  Settings2,
  Eye,
  EyeOff,
} from "lucide-react";
import clsx from "clsx";

// ============================================================
// Category / editability metadata
// ============================================================

const CATEGORY_META: Record<
  string,
  { label: string; icon: typeof Paintbrush; color: string; bgColor: string }
> = {
  colors: {
    label: "Colori",
    icon: Paintbrush,
    color: "text-pink-600",
    bgColor: "bg-pink-50",
  },
  typography: {
    label: "Tipografia",
    icon: Type,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
  },
  spacing: {
    label: "Spaziature",
    icon: Maximize2,
    color: "text-green-600",
    bgColor: "bg-green-50",
  },
  borders: {
    label: "Bordi",
    icon: Square,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
  },
  shadows: {
    label: "Ombre",
    icon: Sun,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
  },
  layout: {
    label: "Layout",
    icon: Ruler,
    color: "text-teal-600",
    bgColor: "bg-teal-50",
  },
};

interface TokenEditorProps {
  projectId: string;
  canEdit: boolean;
}

export function TokenEditor({ projectId, canEdit }: TokenEditorProps) {
  const queryClient = useQueryClient();
  const [localOverrides, setLocalOverrides] = useState<Record<string, string>>(
    {},
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(["colors", "typography"]),
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Fetch resolved tokens
  const {
    data: tokensData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["project-tokens", projectId],
    queryFn: () => projectsApi.getTokens(projectId),
    enabled: Boolean(projectId),
  });

  const tokens = tokensData?.data?.data;

  // Inizializza local overrides dal server
  useEffect(() => {
    if (tokens?.overrides) {
      setLocalOverrides(tokens.overrides);
      setHasChanges(false);
    }
  }, [tokens?.overrides]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: { theme?: string; overrides: Record<string, string> }) =>
      projectsApi.updateTokens(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["project-tokens", projectId],
      });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      setHasChanges(false);
      setSaveMessage({ type: "success", text: "Token salvati" });
      setTimeout(() => setSaveMessage(null), 3000);
    },
    onError: (err: any) => {
      setSaveMessage({
        type: "error",
        text: err?.response?.data?.error?.message || "Errore nel salvataggio",
      });
      setTimeout(() => setSaveMessage(null), 5000);
    },
  });

  // Handler modifica token
  const handleTokenChange = useCallback((key: string, value: string) => {
    setLocalOverrides((prev) => {
      const next = { ...prev };
      if (value === "" || value === undefined) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setHasChanges(true);
  }, []);

  // Reset a valori salvati
  const handleReset = useCallback(() => {
    if (tokens?.overrides) {
      setLocalOverrides(tokens.overrides);
      setHasChanges(false);
    }
  }, [tokens?.overrides]);

  // Salva
  const handleSave = useCallback(() => {
    saveMutation.mutate({ overrides: localOverrides });
  }, [saveMutation, localOverrides]);

  // Export tokens.json
  const handleExport = useCallback(
    async (format: "json" | "css") => {
      try {
        const response = await projectsApi.exportTokens(projectId, format);
        const content =
          format === "css"
            ? response.data
            : JSON.stringify(response.data, null, 2);
        const blob = new Blob([content], {
          type: format === "css" ? "text/css" : "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tokens.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        setSaveMessage({ type: "error", text: "Errore nell'export" });
        setTimeout(() => setSaveMessage(null), 3000);
      }
    },
    [projectId],
  );

  // Raggruppa editableTokens per categoria
  const tokensByCategory = useMemo(() => {
    if (!tokens?.editableTokens) return {};
    const result: Record<string, EditableToken[]> = {};
    for (const t of tokens.editableTokens) {
      if (!showAdvanced && t.definition.editable === "advanced") continue;
      const cat = t.definition.category;
      if (!result[cat]) result[cat] = [];
      result[cat].push(t);
    }
    return result;
  }, [tokens?.editableTokens, showAdvanced]);

  const toggleCategory = useCallback((cat: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  // Calcola conteggio override attivi
  const overrideCount = Object.keys(localOverrides).length;
  const totalEditable =
    tokens?.editableTokens?.filter((t) => t.definition.editable === "client")
      .length || 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="card p-8 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">Caricamento design tokens...</p>
      </div>
    );
  }

  // Error / not available
  if (error || !tokens) {
    const errorResponse = (error as any)?.response;
    const errorMsg = errorResponse?.data?.error?.message;

    // Catalog v7 non disponibile
    const needsV7 =
      errorMsg?.includes("v7") || errorMsg?.includes("design tokens");

    return (
      <div className="card p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Paintbrush className="w-5 h-5 text-gray-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-700">Design Tokens</h3>
            {needsV7 ? (
              <>
                <p className="text-sm text-amber-600 mt-1">
                  Catalogo v7+ richiesto per i design tokens
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Il progetto usa il catalogo v
                  {(error as any)?.response?.data?.catalogVersion || "?"} che
                  non include i design tokens.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Per abilitare questa funzionalità:
                </p>
                <ol className="text-xs text-gray-500 mt-1 list-decimal list-inside space-y-0.5">
                  <li>
                    Vai in <strong>Catalogo</strong> dal menu
                  </li>
                  <li>
                    Importa il{" "}
                    <code className="bg-gray-100 px-1 rounded">
                      product-catalog-v7.json
                    </code>
                  </li>
                  <li>Pubblica la nuova versione</li>
                </ol>
              </>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                {errorMsg ||
                  "Design tokens non disponibili per questo progetto"}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Paintbrush className="w-5 h-5 text-pink-600" />
            <div>
              <h2 className="font-semibold text-gray-900">Design Tokens</h2>
              <p className="text-xs text-gray-500">
                {overrideCount} personalizzazioni su {totalEditable} token
                editabili
                {tokens.themeId && (
                  <>
                    {" "}
                    · Tema:{" "}
                    <span className="font-medium">{tokens.themeId}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Toggle preview */}
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              title={showPreview ? "Nascondi preview" : "Mostra preview"}
            >
              {showPreview ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
            </button>

            {/* Toggle advanced */}
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={showAdvanced}
                onChange={(e) => setShowAdvanced(e.target.checked)}
                className="rounded border-gray-300 text-gray-600"
              />
              <Settings2 className="w-3 h-3" />
              Avanzati
            </label>

            {/* Export */}
            <div className="relative group">
              <button className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1">
                <Download className="w-3 h-3" />
                Export
              </button>
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 hidden group-hover:block z-10 min-w-[120px]">
                <button
                  onClick={() => handleExport("json")}
                  className="w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 text-left"
                >
                  tokens.json
                </button>
                <button
                  onClick={() => handleExport("css")}
                  className="w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 text-left"
                >
                  tokens.css
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Save bar */}
        {canEdit && hasChanges && (
          <div className="mt-3 flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-xs text-amber-700 flex-1">
              Modifiche non salvate
            </span>
            <button
              onClick={handleReset}
              className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
            >
              <RotateCcw className="w-3 h-3 inline mr-1" />
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="px-3 py-1 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1"
            >
              {saveMutation.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Save className="w-3 h-3" />
              )}
              Salva
            </button>
          </div>
        )}

        {/* Save message */}
        {saveMessage && (
          <div
            className={clsx(
              "mt-2 px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5",
              saveMessage.type === "success"
                ? "bg-green-50 text-green-700"
                : "bg-red-50 text-red-700",
            )}
          >
            {saveMessage.type === "success" ? (
              <Check className="w-3 h-3" />
            ) : (
              <AlertTriangle className="w-3 h-3" />
            )}
            {saveMessage.text}
          </div>
        )}
      </div>

      {/* Preview band */}
      {showPreview && (
        <TokenPreviewBand
          resolved={tokens.resolved}
          overrides={localOverrides}
        />
      )}

      {/* Token categories */}
      <div className="divide-y divide-gray-100">
        {Object.entries(tokensByCategory).map(([cat, catTokens]) => {
          const meta = CATEGORY_META[cat] || {
            label: cat,
            icon: Paintbrush,
            color: "text-gray-600",
            bgColor: "bg-gray-50",
          };
          const CatIcon = meta.icon;
          const isExpanded = expandedCategories.has(cat);

          return (
            <div key={cat}>
              {/* Category header */}
              <button
                onClick={() => toggleCategory(cat)}
                className="w-full px-6 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
                <CatIcon className={clsx("w-4 h-4", meta.color)} />
                <span className="font-medium text-sm text-gray-900">
                  {meta.label}
                </span>
                <span className="text-xs text-gray-400">
                  ({catTokens.length})
                </span>
                {/* Mini count override */}
                {catTokens.some((t) => localOverrides[t.key]) && (
                  <span className="ml-auto text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                    {catTokens.filter((t) => localOverrides[t.key]).length} mod.
                  </span>
                )}
              </button>

              {/* Token editors */}
              {isExpanded && (
                <div className="px-6 pb-4 space-y-3">
                  {catTokens.map((token) => (
                    <TokenField
                      key={token.key}
                      token={token}
                      currentValue={localOverrides[token.key] ?? ""}
                      onChange={(value) => handleTokenChange(token.key, value)}
                      canEdit={canEdit}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {Object.keys(tokensByCategory).length === 0 && (
        <div className="p-8 text-center">
          <Paintbrush className="w-8 h-8 mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">Nessun token editabile</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Token Preview Band — mini preview visivo
// ============================================================

function TokenPreviewBand({
  resolved,
  overrides,
}: {
  resolved: Record<string, string>;
  overrides: Record<string, string>;
}) {
  // Merge override locali per preview live
  const merged = { ...resolved };
  for (const [k, v] of Object.entries(overrides)) {
    if (v) merged[k] = v;
  }

  const primary = merged["color.primary"] || "#1e3a5f";
  const secondary = merged["color.secondary"] || "#d4a574";
  const bg = merged["color.bg"] || "#ffffff";
  const bgAlt = merged["color.bg.alt"] || "#f5f5f5";
  const text = merged["color.text"] || "#1a1a1a";
  const textSec = merged["color.text.secondary"] || "#6b7280";
  const border = merged["color.border"] || "#e5e5e5";
  const heading = merged["font.heading"] || "serif";
  const body = merged["font.body"] || "sans-serif";
  const radius = merged["radius.md"] || "0.5rem";

  return (
    <div
      className="px-6 py-4 border-b border-gray-100"
      style={{ backgroundColor: bg }}
    >
      <div className="flex items-start gap-4">
        {/* Color swatches */}
        <div className="flex gap-1.5">
          <div
            className="w-8 h-8 rounded-lg border"
            style={{ backgroundColor: primary, borderColor: border }}
            title="Primary"
          />
          <div
            className="w-8 h-8 rounded-lg border"
            style={{ backgroundColor: secondary, borderColor: border }}
            title="Secondary"
          />
          <div
            className="w-8 h-8 rounded-lg border"
            style={{ backgroundColor: bgAlt, borderColor: border }}
            title="Bg Alt"
          />
        </div>

        {/* Typography preview */}
        <div className="flex-1 min-w-0">
          <p
            style={{ fontFamily: heading, color: text }}
            className="text-lg font-bold leading-tight truncate"
          >
            Titolo esempio
          </p>
          <p
            style={{ fontFamily: body, color: textSec }}
            className="text-sm mt-0.5 truncate"
          >
            Testo corpo di esempio con font {body}
          </p>
        </div>

        {/* Button preview */}
        <button
          className="px-3 py-1.5 text-xs text-white flex-shrink-0"
          style={{ backgroundColor: primary, borderRadius: radius }}
        >
          Pulsante
        </button>

        {/* Card preview */}
        <div
          className="w-24 h-12 flex-shrink-0 border flex items-center justify-center"
          style={{
            backgroundColor: bg,
            borderColor: border,
            borderRadius: radius,
            boxShadow: merged["shadow.md"] || "none",
          }}
        >
          <span
            style={{ fontFamily: body, color: textSec }}
            className="text-xs"
          >
            Card
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Single Token Field Editor
// ============================================================

interface TokenFieldProps {
  token: EditableToken;
  currentValue: string;
  onChange: (value: string) => void;
  canEdit: boolean;
}

function TokenField({
  token,
  currentValue,
  onChange,
  canEdit,
}: TokenFieldProps) {
  const { key, definition: def, defaultValue, themeValue } = token;
  const displayValue = currentValue || themeValue || defaultValue;
  const isOverridden = Boolean(currentValue);
  const isAdvanced = def.editable === "advanced";

  // Determina quale editor usare
  const renderEditor = () => {
    if (!canEdit) {
      return <TokenValueDisplay value={displayValue} type={def.type} />;
    }

    switch (def.type) {
      case "color":
        return (
          <ColorEditor
            value={currentValue}
            fallback={themeValue || defaultValue}
            onChange={onChange}
          />
        );
      case "fontFamily":
        return (
          <FontFamilyEditor
            value={currentValue}
            fallback={themeValue || defaultValue}
            options={def.options}
            onChange={onChange}
          />
        );
      case "size":
      case "number":
        if (def.control?.type === "slider") {
          return (
            <SliderEditor
              value={currentValue}
              fallback={themeValue || defaultValue}
              min={def.control.min as number}
              max={def.control.max as number}
              step={def.control.step as number}
              unit={def.control.unit as string}
              onChange={onChange}
            />
          );
        }
        if (def.control?.type === "preset") {
          return (
            <PresetEditor
              value={currentValue}
              fallback={themeValue || defaultValue}
              options={
                (def.control.options as Array<{
                  value: string;
                  label: string;
                }>) || []
              }
              onChange={onChange}
            />
          );
        }
        return (
          <TextEditor
            value={currentValue}
            fallback={themeValue || defaultValue}
            onChange={onChange}
          />
        );
      case "shadow":
        if (def.control?.type === "preset") {
          return (
            <PresetEditor
              value={currentValue}
              fallback={themeValue || defaultValue}
              options={
                (def.control.options as Array<{
                  value: string;
                  label: string;
                }>) || []
              }
              onChange={onChange}
            />
          );
        }
        return (
          <TextEditor
            value={currentValue}
            fallback={themeValue || defaultValue}
            onChange={onChange}
          />
        );
      default:
        return (
          <TextEditor
            value={currentValue}
            fallback={themeValue || defaultValue}
            onChange={onChange}
          />
        );
    }
  };

  return (
    <div
      className={clsx(
        "flex items-start gap-4 p-3 rounded-lg border transition-colors",
        isOverridden
          ? "border-blue-200 bg-blue-50/50"
          : "border-gray-100 bg-white",
        isAdvanced && "opacity-80",
      )}
    >
      {/* Label */}
      <div className="w-44 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-gray-800">{def.label}</span>
          {isAdvanced && (
            <span title="Token avanzato">
              <Settings2 className="w-3 h-3 text-amber-500" />
            </span>
          )}
        </div>
        <code className="text-xs text-gray-400 font-mono">{key}</code>
        {def.description && (
          <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
            {def.description}
          </p>
        )}
      </div>

      {/* Editor */}
      <div className="flex-1 min-w-0">{renderEditor()}</div>

      {/* Reset button */}
      {canEdit && isOverridden && (
        <button
          onClick={() => onChange("")}
          className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
          title="Ripristina valore tema/default"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Origin indicator */}
      <div className="flex-shrink-0 w-6">
        {isOverridden ? (
          <span title="Personalizzato">
            <Palette className="w-4 h-4 text-blue-500" />
          </span>
        ) : themeValue ? (
          <span title="Da tema">
            <Zap className="w-4 h-4 text-amber-400" />
          </span>
        ) : null}
      </div>
    </div>
  );
}

// ============================================================
// Editors specializzati
// ============================================================

function ColorEditor({
  value,
  fallback,
  onChange,
}: {
  value: string;
  fallback: string;
  onChange: (v: string) => void;
}) {
  const display = value || fallback;
  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <input
          type="color"
          value={display.startsWith("#") ? display : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-8 rounded border border-gray-300 cursor-pointer p-0"
        />
      </div>
      <input
        type="text"
        value={value}
        placeholder={fallback}
        onChange={(e) => onChange(e.target.value)}
        className="w-28 px-2 py-1.5 text-xs font-mono border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
      />
      {/* Preview swatch */}
      <div
        className="w-8 h-8 rounded border border-gray-200"
        style={{ backgroundColor: display }}
      />
    </div>
  );
}

function FontFamilyEditor({
  value,
  fallback,
  options,
  onChange,
}: {
  value: string;
  fallback: string;
  options?: Array<{ value: string; label: string; style?: string }>;
  onChange: (v: string) => void;
}) {
  if (options && options.length > 0) {
    return (
      <div className="space-y-1.5">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Tema ({fallback})</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
              {opt.style ? ` — ${opt.style}` : ""}
            </option>
          ))}
        </select>
        <p
          className="text-sm text-gray-500 truncate"
          style={{ fontFamily: value || fallback }}
        >
          Anteprima: Il patrimonio culturale valdese
        </p>
      </div>
    );
  }
  return (
    <input
      type="text"
      value={value}
      placeholder={fallback}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
    />
  );
}

function SliderEditor({
  value,
  fallback,
  min,
  max,
  step,
  unit,
  onChange,
}: {
  value: string;
  fallback: string;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (v: string) => void;
}) {
  const numericValue = value ? parseFloat(value) : parseFloat(fallback) || min;

  return (
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={numericValue}
        onChange={(e) => {
          const num = parseFloat(e.target.value);
          onChange(unit ? `${num}${unit}` : String(num));
        }}
        className="flex-1 h-1.5 accent-gray-700"
      />
      <span className="text-xs font-mono text-gray-600 w-16 text-right">
        {value || fallback}
      </span>
    </div>
  );
}

function PresetEditor({
  value,
  fallback,
  options,
  onChange,
}: {
  value: string;
  fallback: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  const currentValue = value || fallback;
  return (
    <div className="flex flex-wrap gap-1.5">
      <button
        onClick={() => onChange("")}
        className={clsx(
          "px-2.5 py-1 text-xs rounded-lg border transition-colors",
          !value
            ? "border-gray-300 bg-gray-100 text-gray-700 font-medium"
            : "border-gray-200 text-gray-500 hover:border-gray-300",
        )}
      >
        Tema
      </button>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={clsx(
            "px-2.5 py-1 text-xs rounded-lg border transition-colors",
            currentValue === opt.value && value
              ? "border-blue-300 bg-blue-50 text-blue-700 font-medium"
              : "border-gray-200 text-gray-500 hover:border-gray-300",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function TextEditor({
  value,
  fallback,
  onChange,
}: {
  value: string;
  fallback: string;
  onChange: (v: string) => void;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={fallback}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 text-sm font-mono border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500"
    />
  );
}

function TokenValueDisplay({ value, type }: { value: string; type: string }) {
  const isColor = type === "color" || /^(#|rgb|hsl)/.test(value);
  return (
    <div className="flex items-center gap-2">
      {isColor && (
        <div
          className="w-6 h-6 rounded border border-gray-200"
          style={{ backgroundColor: value }}
        />
      )}
      <code className="text-xs font-mono text-gray-600">{value}</code>
    </div>
  );
}
